/**
 * Ian - 2026-10-20 - Product video tracking for GA4 (Rosie's ask, 12 Oct 2026).
 * Loaded by snippets/ian-video-tracking.liquid, only on product pages with a Shopify-hosted video.
 *
 * Why a script at all: product videos are uploaded to Shopify, not YouTube, so GA4's enhanced
 * measurement never sees them. The Google & YouTube app puts gtag on the page once the visitor
 * accepts cookies, so these events land in the same GA4 sessions as its own page_view and
 * add_to_cart. No cookie consent, no gtag, nothing sent.
 *
 * Events, all sent to G-Z5HLB0RCNZ only, each carrying video_title (the product title, a built-in
 * GA4 dimension, so no custom dimension is needed to report by product):
 *   video_product_view        once per page view of a product that has a video (the denominator)
 *   video_start               first play of each video on the page
 *   video_progress            at 25, 50 and 75% (video_percent)
 *   video_complete            at 95%, once, so it still counts if the last second is skipped
 *   video_page_atc_watched    added this product to the basket after starting its video
 *   video_page_atc_unwatched  added this product to the basket without starting it
 *
 * A video starts when it's clicked, and also when its slide becomes active in the gallery
 * (assets/media-gallery.js playActiveMedia), so swiping onto it counts as a start. Progress and
 * complete are the better read of real watching.
 */
(() => {
  const GA_ID = 'G-Z5HLB0RCNZ';
  const WATCHED_KEY = 'ianVideoWatched';
  const MILESTONES = [25, 50, 75];

  const dataEl = document.getElementById('ian-video-tracking-data');
  if (!dataEl || window.ianVideoTracking) return;
  window.ianVideoTracking = true;

  let product;
  try {
    product = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  const send = (name, params = {}) => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', name, {
      send_to: GA_ID,
      video_provider: 'shopify',
      video_title: product.title,
      video_url: product.url,
      product_template: product.template,
      ...params
    });
  };

  const readWatched = () => {
    try {
      return JSON.parse(sessionStorage.getItem(WATCHED_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };

  const markWatched = () => {
    const ids = readWatched();
    if (ids.includes(product.productId)) return;
    ids.push(product.productId);
    try {
      sessionStorage.setItem(WATCHED_KEY, JSON.stringify(ids));
    } catch (e) {
      // Storage blocked: the add to basket on this page still reads the in-memory flag.
    }
  };

  let watchedHere = false;
  const state = new WeakMap();

  const isProductVideo = (el) => el && el.tagName === 'VIDEO' && el.closest('media-gallery deferred-media');

  const videoParams = (video, extra = {}) => ({
    video_duration: Math.round(video.duration || 0),
    video_current_time: Math.round(video.currentTime || 0),
    ...extra
  });

  // Media events don't bubble, so listen in the capture phase to catch videos the
  // gallery injects from its <template> after the page has loaded.
  document.addEventListener('play', (event) => {
    const video = event.target;
    if (!isProductVideo(video)) return;
    let s = state.get(video);
    if (!s) {
      s = { started: false, reached: new Set(), complete: false };
      state.set(video, s);
    }
    if (s.started) return;
    s.started = true;
    watchedHere = true;
    markWatched();
    send('video_start', videoParams(video));
  }, true);

  document.addEventListener('timeupdate', (event) => {
    const video = event.target;
    if (!isProductVideo(video) || !video.duration) return;
    const s = state.get(video);
    if (!s || s.complete) return;
    const percent = (video.currentTime / video.duration) * 100;
    MILESTONES.forEach((m) => {
      if (percent >= m && !s.reached.has(m)) {
        s.reached.add(m);
        send('video_progress', videoParams(video, { video_percent: m }));
      }
    });
    if (percent >= 95) {
      s.complete = true;
      send('video_complete', videoParams(video, { video_percent: 100 }));
    }
  }, true);

  document.addEventListener('ended', (event) => {
    const video = event.target;
    if (!isProductVideo(video)) return;
    const s = state.get(video);
    if (!s || s.complete) return;
    s.complete = true;
    send('video_complete', videoParams(video, { video_percent: 100 }));
  }, true);

  // on:cart:add fires from the main product form and from the new PDP's product cards
  // (assets/ian-pdp-cart.js); only count variants of this page's own product.
  document.addEventListener('on:cart:add', (event) => {
    const variantId = event.detail && Number(event.detail.variantId);
    if (!variantId || !product.variantIds.includes(variantId)) return;
    const watched = watchedHere || readWatched().includes(product.productId);
    send(watched ? 'video_page_atc_watched' : 'video_page_atc_unwatched');
  });

  // gtag only exists once cookies are accepted, which can be after this script runs, so wait
  // for it briefly, and again when Shopify's cookie banner reports a choice.
  let viewSent = false;
  const waitThenSendView = () => {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (!viewSent && typeof window.gtag === 'function') {
        viewSent = true;
        send('video_product_view');
      }
      if (viewSent || tries >= 20) clearInterval(timer);
    }, 500);
  };
  waitThenSendView();
  document.addEventListener('visitorConsentCollected', waitThenSendView);
})();
